import { useQuery } from "@tanstack/react-query";
import { EVIDENCE_EXT, EVIDENCE_MAX_BYTES, EVIDENCE_MIME, type EvidenceDetailDto, type EvidenceDto, type EvidenceTargetType, type Page, type UploadUrlDto } from "@audit-plan/shared";
import { ApiError, api, apiUrl } from "./client";
import { unwrap } from "./audits";

export type EvidenceFilters = { supplierId?: string; auditId?: string; targetType?: string; targetId?: string };

export function useEvidenceList(f: EvidenceFilters, enabled = true) {
  return useQuery({ queryKey: ["evidence", "b", f], queryFn: async () => unwrap(await api.get<EvidenceDto[] | Page<EvidenceDto>>("/evidence", f)), enabled });
}

/** Evidenza con fornitore e audit (pagina con URL e breadcrumb). */
export function useEvidence(id?: string | null) {
  return useQuery({ queryKey: ["evidence", "one", id], queryFn: () => api.get<EvidenceDetailDto>(`/evidence/${id}`), enabled: !!id });
}

/** Ruolo dell'evidenza dalla prima riga della descrizione (import dello storico): gruppo e ordine nell'elenco. */
export type EvidenceRole = "report" | "outcome" | "cap" | "other";
export const evidenceRole = (description?: string | null): EvidenceRole => {
  const first = (description ?? "").split("\n")[0] ?? "";
  return /^rapporto/i.test(first) ? "report" : /^esito/i.test(first) ? "outcome" : /^azione correttiva/i.test(first) ? "cap" : "other";
};
export const ROLE_ORDER: EvidenceRole[] = ["report", "outcome", "cap", "other"];

export interface UploadCtx {
  supplierId: string;
  auditId?: string | null;
  target: { type: EvidenceTargetType; id: string };
}

export const fileExt = (name: string) => (name.split(".").pop() || "").toLowerCase();

/** Errore di validazione client (chiave i18n in message). */
export class UploadError extends Error {
  constructor(public key: "b_ext_bad" | "b_file_big") {
    super(key);
  }
}

export function checkFile(file: File) {
  if (!(EVIDENCE_EXT as readonly string[]).includes(fileExt(file.name))) throw new UploadError("b_ext_bad");
  if (file.size > EVIDENCE_MAX_BYTES) throw new UploadError("b_file_big");
}

export const IMAGE_MAX_SIDE = 2560;
export const IMAGE_QUALITY = 0.85;
const IMAGE_EXT = ["jpg", "jpeg", "png", "heic", "heif"];

const sleep = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((res, rej) => {
    const id = setTimeout(res, ms);
    signal?.addEventListener("abort", () => {
      clearTimeout(id);
      rej(new DOMException("Aborted", "AbortError"));
    });
  });

export const isAbort = (e: unknown) => e instanceof DOMException && e.name === "AbortError";

/**
 * Riduce un'immagine nel browser: lato lungo max 2560 px, JPEG 0.85, orientamento EXIF applicato.
 * Se non riesce a decodificarla (es. HEIC fuori da Safari) o il risultato non e' piu' piccolo, restituisce l'originale.
 * `keepExt`: per le nuove versioni di un'evidenza il formato non puo' cambiare.
 */
export async function prepareImage(file: File, keepExt?: string): Promise<File> {
  const ext = fileExt(file.name);
  if (!IMAGE_EXT.includes(ext) || typeof createImageBitmap !== "function") return file;
  if (keepExt && !["jpg", "jpeg"].includes(keepExt)) return file; // nuova versione: il formato non cambia
  let bmp: ImageBitmap | null = null;
  try {
    bmp = await createImageBitmap(file, { imageOrientation: "from-image" });
  } catch {
    try {
      bmp = await createImageBitmap(file);
    } catch {
      return file; // non decodificabile (HEIC fuori da Safari): si carica cosi' com'e'
    }
  }
  try {
    const scale = Math.min(1, IMAGE_MAX_SIDE / Math.max(bmp.width, bmp.height));
    const w = Math.max(1, Math.round(bmp.width * scale));
    const h = Math.max(1, Math.round(bmp.height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) return file;
    ctx.fillStyle = "#fff"; // PNG con trasparenza: sfondo bianco nel JPEG
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(bmp, 0, 0, w, h);
    const blob = await new Promise<Blob | null>((res) => canvas.toBlob(res, "image/jpeg", IMAGE_QUALITY));
    canvas.width = canvas.height = 0;
    if (!blob || blob.type !== "image/jpeg") return file;
    if (blob.size >= file.size) return file;
    const outExt = keepExt ?? (ext === "jpeg" ? "jpeg" : "jpg");
    const base = file.name.replace(/\.[^.]+$/, "");
    return new File([blob], `${base}.${outExt}`, { type: "image/jpeg", lastModified: file.lastModified });
  } finally {
    bmp.close?.();
  }
}

export interface UploadOpts {
  onProgress?: (pct: number) => void;
  /** Chiamata prima di ogni nuovo tentativo (attempt = 1 o 2). */
  onRetry?: (attempt: number) => void;
  signal?: AbortSignal;
  /** Tentativi aggiuntivi sul PUT e sulla conferma, con attesa crescente (default 2). */
  retries?: number;
}

class HttpFail extends Error {
  constructor(public status: number) {
    super(`Upload S3 ${status}`);
  }
}

export function putXhr(url: string, file: Blob, headers: Headers, opts: UploadOpts) {
  return new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    xhr.withCredentials = false;
    headers.forEach((v, k) => {
      try {
        xhr.setRequestHeader(k, v);
      } catch {
        /* header vietato dal browser */
      }
    });
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) opts.onProgress?.(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new HttpFail(xhr.status)));
    xhr.onerror = () => reject(new Error("network"));
    xhr.ontimeout = () => reject(new Error("timeout"));
    xhr.onabort = () => reject(new DOMException("Aborted", "AbortError"));
    opts.signal?.addEventListener("abort", () => xhr.abort());
    if (opts.signal?.aborted) return reject(new DOMException("Aborted", "AbortError"));
    xhr.send(file);
  });
}

async function withRetry<T>(fn: () => Promise<T>, opts: UploadOpts): Promise<T> {
  const max = opts.retries ?? 2;
  for (let attempt = 0; ; attempt++) {
    try {
      return await fn();
    } catch (e) {
      const st = e instanceof HttpFail || e instanceof ApiError ? e.status : 0;
      const fatal = isAbort(e) || (st >= 400 && st < 500 && st !== 408 && st !== 429);
      if (fatal || attempt >= max) throw e;
      opts.onRetry?.(attempt + 1);
      opts.onProgress?.(0);
      await sleep(1500 * (attempt + 1), opts.signal);
    }
  }
}

export async function sha256Hex(file: Blob): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", await file.arrayBuffer());
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

/**
 * Upload in 3 passi: URL firmato -> PUT diretto su S3 (XHR, senza cookie, con avanzamento) -> conferma.
 * Con evidenceId carica una nuova versione di un'evidenza esistente.
 */
export async function uploadEvidence(file: File, ctx: UploadCtx, description = "", evidenceId?: string, opts: UploadOpts = {}): Promise<EvidenceDto> {
  checkFile(file);
  // il backend pretende che il tipo coincida con l'estensione
  const contentType = EVIDENCE_MIME[fileExt(file.name)] || file.type || "application/octet-stream";
  // sha256 del file: il bucket ha Object Lock e accetta il PUT solo con il checksum firmato
  const sha256 = await sha256Hex(file);
  const up = await api.post<UploadUrlDto>("/evidence/upload-url", {
    sha256,
    supplierId: ctx.supplierId,
    auditId: ctx.auditId ?? null,
    target: ctx.target,
    name: file.name,
    size: file.size,
    contentType,
    description,
    ...(evidenceId ? { evidenceId } : {}),
  });
  const headers = new Headers(up.headers ?? {});
  if (!headers.has("content-type")) headers.set("content-type", contentType);
  await withRetry(() => putXhr(up.url, file, headers, opts), opts);
  opts.onProgress?.(100);
  const done = await withRetry(() => api.post<EvidenceDto | undefined>(`/evidence/${up.evidenceId}/versions/${up.versionId}/confirm`), opts);
  return done && done.id ? done : ({ id: up.evidenceId } as EvidenceDto);
}

export const reviewEvidence = (id: string, status: "approved" | "rejected", note?: string) => api.post(`/evidence/${id}/review`, { status, note });

export const evidenceDownloadUrl = (id: string, v: number, inline = false) => apiUrl(`/evidence/${id}/versions/${v}/download`, inline ? { inline: 1 } : undefined);
